import axios from 'axios';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { requestApproval } from 'src/api/stoApi';
import { Colors } from 'src/components/utils/Theme';
import { Asset } from 'src/models';
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

interface Props {
  open: boolean;
  asset: Asset;
  handleClose: () => void;
  triggerRefresh: () => void;
}

export const RequestApprovalDialog: FC<Props> = ({ open, asset, handleClose, triggerRefresh }) => {
  const [t] = useTranslation();
  const [isLoading, setLoading] = useState(false);
  const [dialogState, setDialogState] = useState<'init' | 'error' | 'success'>('init');
  const [errors, setErrors] = useState<string[]>([]);
  const preError1 = !asset.investmentDetails?.ISIN;

  const allPreErrors = preError1;

  const handleCloseAndReset = () => {
    handleClose();
    setDialogState('init');
    setErrors([]);
  };

  const postRequestApproval = async () => {
    setLoading(true);
    try {
      const changeStatus = await requestApproval(asset.id);
      if (changeStatus.status === 200) {
        setDialogState('success');
        triggerRefresh();
      }
    } catch (error) {
      setDialogState('error');
      if (axios.isAxiosError(error) && error.response) {
        setErrors(['Something went wrong. Please try again later.']);
        if (error.response.status === 412) {
          setErrors(error.response.data?.message.split(','));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseAndReset}>
      {/* Has all pre errors */}
      {allPreErrors && (
        <>
          <DialogTitle>{t('dashboard.assets.dialog.requestapproval.preerrors.headline')}</DialogTitle>
          <DialogContent>
            <Typography>{t('dashboard.assets.dialog.requestapproval.preerrors.message')}</Typography>
            <ul
              style={{
                paddingTop: '24px',
                paddingLeft: '24px',
                listStyleType: 'disc',
              }}
            >
              {preError1 && <li>{t('dashboard.assets.dialog.requestapproval.preerrors.message.point1')}</li>}
            </ul>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" disableElevation color="primary" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.requestapproval.preerrors.ok')}
            </Button>
          </DialogActions>
        </>
      )}

      {/* No or not all pre errors */}
      {!allPreErrors && dialogState === 'init' && (
        // init state
        <>
          <DialogTitle>{t('dashboard.assets.dialog.requestapproval.init.headline')}</DialogTitle>
          <DialogContent>
            <Typography>{t('dashboard.assets.dialog.requestapproval.init.message')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.requestapproval.init.cancel')}
            </Button>
            <LoadingButton loading={isLoading} variant="contained" disableElevation color="primary" onClick={postRequestApproval}>
              {t('dashboard.assets.dialog.requestapproval.init.confirm')}
            </LoadingButton>
          </DialogActions>
        </>
      )}

      {!allPreErrors && dialogState === 'success' && (
        // success state
        <>
          <DialogTitle>{t('dashboard.assets.dialog.requestapproval.success.headline')}</DialogTitle>
          <DialogContent>
            <Typography>{t('dashboard.assets.dialog.requestapproval.success.message')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" disableElevation color="primary" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.requestapproval.success.ok')}
            </Button>
          </DialogActions>
        </>
      )}

      {!allPreErrors && dialogState === 'error' && (
        // error state
        <>
          <DialogTitle>{t('dashboard.assets.dialog.requestapproval.error.headline')}</DialogTitle>
          <DialogContent>
            <Typography>{t('dashboard.assets.dialog.requestapproval.error.message')}</Typography>
            <ul
              style={{
                paddingTop: '24px',
                paddingLeft: '24px',
                listStyleType: 'disc',
                color: Colors.ERROR,
              }}
            >
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" disableElevation color="primary" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.requestapproval.error.ok')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
